import { useCallback, useEffect, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { useQuery, makeVar } from '@apollo/client';
import Pagination from '@material-ui/lab/Pagination';
import { GigTable } from './GigTable';
import {
  GigsByPriorityBucket,
  GigsByBucket,
  GroupedGigsByPriorityBucket,
  GroupedGigsByBucket
} from '../queries/myBoards';

import Loader from './Loader';
import { getBucketFromQueryResult } from '../Helper';
import {
  ORDER_BY,
  GIGS_BY_BUCKET_PAGINATION_LIMIT,
  POLLING_INTERVALS
} from '../constants';
import { useStyles } from '../theme';
import { useIsFeatureFlagEnabled } from '../contexts/feature-flag-context';
import { DriverTable } from './DriverTable';

const styles = (theme) => ({
  red: {
    backgroundColor: '#EF9A9A'
  },
  orange: {
    backgroundColor: '#FFCC80'
  },
  yellow: {
    backgroundColor: '#FFF59D'
  },
  green: {
    backgroundColor: '#A5D6A7'
  },
  gray: {
    backgroundColor: 'lightgray'
  },
  column: {
    flexBasis: '25%'
  },
  gigRow: {
    flex: '0 0 100%'
  },
  panelDetails: {
    padding: 0,
    flexDirection: 'column'
  },
  title: {
    fontSize: 26,
    opacity: 0.8
  },
  section: {
    marginBottom: 15
  },
  popover: {
    padding: theme.spacing(1),
    pointerEvents: 'none'
  },
  infoIcon: {
    width: 20,
    paddingLeft: 8
  },
  titleWithIcon: {
    display: 'inline-flex',
    verticalAlign: 'middle',
    alignItems: 'center'
  },
  titlePadding: {
    maxHeight: 60,
    minHeight: 60,
    paddingLeft: 32,
    paddingRight: 72
  }
});

const filterColorMap = {
  find_driver: 'red',
  contact_driver: 'orange',
  monitor: 'yellow',
  dont_worry: 'green',

  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'green',
  minimal: 'gray'
};

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 10px 0;
`;

export const loadedGigCountForBucket = makeVar(0);

export const Bucket = ({
  boardId,
  bucketId,
  priority,
  rule,
  gigCount,
  showFirstAndLastName,
  isStandardBoard = true,
  headerColor,
  appliedFilters
}) => {
  const driverBucketFlag = useIsFeatureFlagEnabled('vip-fe-add-driver-buckets');
  const duplicateQueryFixFlag = useIsFeatureFlagEnabled('vip-fe-duplicate-query-fix');
  const groupedQuery = isStandardBoard
    ? GroupedGigsByBucket
    : GroupedGigsByPriorityBucket;
  const ungroupedQuery = isStandardBoard ? GigsByBucket : GigsByPriorityBucket;
  const query = driverBucketFlag ? groupedQuery : ungroupedQuery;
  const vars = useMemo(() => {
    const baseVars = {
      boardId,
      bucketId,
      filter: appliedFilters
    };
    return isStandardBoard
      ? baseVars
      : { ...baseVars, priority, title: rule ? rule : null };
  }, [boardId, bucketId, appliedFilters, priority, rule, isStandardBoard]);

  const totalPages = Math.ceil(gigCount / GIGS_BY_BUCKET_PAGINATION_LIMIT);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const { data, loading, error, fetchMore } = useQuery(query, {
    variables: {
      ...vars,
      pageNumber: currentPageNumber,
      recordsPerPage: GIGS_BY_BUCKET_PAGINATION_LIMIT,
      order: ORDER_BY[2].value
    },
    pollInterval: POLLING_INTERVALS.BUCKET_EXPANDED,
    nextFetchPolicy: 'network-only'
  });

  const classes = useStyles(styles);

  const headerColorClass = headerColor
    ? classes[headerColor]
    : classes[filterColorMap[priority]];

  const getNewPage = useCallback(
    (pageNumber) => {
      fetchMore({
        fetchPolicy: 'network-only',
        variables: {
          ...vars,
          pageNumber,
          recordsPerPage: GIGS_BY_BUCKET_PAGINATION_LIMIT,
          order: ORDER_BY[1].value
        }
      });
    },
    [fetchMore, vars]
  );

  useEffect(() => {
    if (!duplicateQueryFixFlag) {
      setCurrentPageNumber(1);
      getNewPage(1);
    }
    // intentional to only run when data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, getNewPage, duplicateQueryFixFlag]);

  if (loading) {
    return <Loader />;
  }
  if (error) {
    return <div>{error.message}</div>;
  }
  if (data) {
    const fetchedBucket = getBucketFromQueryResult(
      data,
      boardId,
      bucketId,
      isStandardBoard ? 'buckets' : 'bucketsByPriority'
    );
    const Component = driverBucketFlag ? DriverTable : GigTable;
    return (
      <Component
        classes={classes}
        key={bucketId}
        gigs={
          driverBucketFlag ? fetchedBucket?.groupedGigs : fetchedBucket?.gigs
        }
        boardId={boardId}
        showFirstAndLastName={data?.myBoards[0]?.showFirstAndLastName}
        textlineGroupUuid={data?.myBoards[0]?.textlineGroupUuid}
        bucketId={bucketId}
        headerColorClass={driverBucketFlag && headerColorClass}
      >
        {totalPages && (
          <PaginationContainer>
            <Pagination
              count={totalPages}
              onChange={(_, page) => {
                getNewPage(page);
                setCurrentPageNumber(page);
              }}
              page={currentPageNumber}
              size="small"
              color="primary"
              variant="outlined"
            />
          </PaginationContainer>
        )}
      </Component>
    );
  }
};
